import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/feature/Header';
import Footer from './home/components/Footer';

export default function NotFound() {
  const [searchQuery, setSearchQuery] = useState('');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const popularPages = [
    { name: '产品中心', path: '/products', icon: 'ri-product-hunt-line' },
    { name: '服务介绍', path: '/services', icon: 'ri-service-line' },
    { name: '客户案例', path: '/cases', icon: 'ri-file-list-3-line' },
    { name: '资源中心', path: '/resources', icon: 'ri-book-open-line' },
    { name: '关于我们', path: '/about', icon: 'ri-information-line' },
    { name: '联系我们', path: '/contact', icon: 'ri-mail-line' }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.REACT_APP_NAVIGATE(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-teal-50 pt-20 flex flex-col relative overflow-hidden">
        {/* 动态背景装饰 */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(20, 184, 166, 0.1) 0%, transparent 50%)`
          }}
        />
        
        {/* 浮动装饰元素 */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-teal-200/30 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-cyan-200/30 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-24 h-24 bg-teal-300/20 rounded-full blur-xl animate-pulse delay-500"></div>

        <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-16 relative z-10">
          {/* 404动画数字 */}
          <div className="relative mb-8">
            <h1 className="text-[180px] md:text-[240px] font-black text-transparent bg-clip-text bg-gradient-to-br from-teal-400 via-cyan-500 to-teal-600 leading-none select-none">
              404
            </h1>
            <div className="absolute inset-0 text-[180px] md:text-[240px] font-black text-teal-600/10 blur-2xl leading-none select-none">
              404
            </div>
          </div>

          {/* 错误信息 */}
          <div className="max-w-2xl mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              哎呀！页面走丢了
            </h2>
            <p className="text-lg md:text-xl text-gray-600 mb-2">
              您访问的页面可能已被移动、删除，或者从未存在过
            </p>
            <p className="text-base text-gray-500">
              别担心，让我们帮您找到正确的方向
            </p>
          </div>

          {/* 搜索框 */}
          <form onSubmit={handleSearch} className="w-full max-w-2xl mb-12">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索您想要的内容..."
                className="w-full px-6 py-4 pr-32 text-lg border-2 border-gray-200 rounded-2xl focus:border-teal-500 focus:outline-none transition-all shadow-lg"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl hover:from-teal-700 hover:to-cyan-700 transition-all whitespace-nowrap font-medium"
              >
                <i className="ri-search-line mr-2"></i>
                搜索
              </button>
            </div>
          </form>

          {/* 快速导航 */}
          <div className="w-full max-w-4xl mb-12">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">或者访问这些热门页面</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {popularPages.map((page, index) => (
                <Link
                  key={index}
                  to={page.path}
                  className="group p-6 bg-white rounded-2xl border-2 border-gray-100 hover:border-teal-500 hover:shadow-xl transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-teal-100 to-cyan-100 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                    <i className={`${page.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                    {page.name}
                  </h4>
                </Link>
              ))}
            </div>
          </div>

          {/* 主要操作按钮 */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl hover:from-teal-700 hover:to-cyan-700 transition-all whitespace-nowrap font-semibold shadow-lg hover:shadow-xl cursor-pointer"
            >
              <i className="ri-home-line mr-2 w-5 h-5 flex items-center justify-center"></i>
              返回首页
            </Link>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:border-teal-600 hover:text-teal-600 hover:bg-teal-50 transition-all whitespace-nowrap font-semibold cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2 w-5 h-5 flex items-center justify-center"></i>
              返回上一页
            </button>
          </div>

          {/* 帮助提示 */}
          <div className="mt-12 p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 max-w-2xl">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                <i className="ri-lightbulb-line text-xl text-teal-600"></i>
              </div>
              <div className="text-left">
                <h4 className="font-semibold text-gray-900 mb-2">需要帮助？</h4>
                <p className="text-gray-600 text-sm mb-3">
                  如果您认为这是一个错误，或者需要技术支持，请随时联系我们的团队
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer"
                >
                  联系技术支持
                  <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
